import { ADMIN_EMAILS } from "@pluck/api/admin-emails";
import {
	createHashHistory,
	createRootRoute,
	createRoute,
	createRouter,
	Outlet,
	redirect,
} from "@tanstack/react-router";
import { AppShell } from "@/components/app-shell";
import { getSessionUser } from "@/lib/auth-client";
import { isDesktop } from "@/lib/desktop";
import { AccountPage } from "@/routes/account";
import { BoardPage } from "@/routes/board";
import { BoardsPage } from "@/routes/boards";
import { LibraryPage } from "@/routes/library";
import { LoginPage } from "@/routes/login";
import { PluckDetailPage } from "@/routes/pluck-detail";
import { SuccessPage } from "@/routes/success";

function RootLayout() {
	return (
		<>
			<div aria-hidden="true" className="desktop-drag-strip" />
			<Outlet />
		</>
	);
}

const rootRoute = createRootRoute({
	component: RootLayout,
});

export const loginRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/login",
	validateSearch: (
		search: Record<string, unknown>
	): { redirect?: string; ref?: string } => ({
		redirect: typeof search.redirect === "string" ? search.redirect : undefined,
		/** Invite code from /invite/<code>; attributed after sign-in. */
		ref: typeof search.ref === "string" ? search.ref : undefined,
	}),
	component: LoginPage,
});

export const appRoute = createRoute({
	getParentRoute: () => rootRoute,
	id: "app",
	beforeLoad: async ({ location }) => {
		const user = await getSessionUser();
		if (!user) {
			throw redirect({ to: "/login", search: { redirect: location.href } });
		}
		return { user, isAdmin: ADMIN_EMAILS.includes(user.email) };
	},
	component: AppShell,
});

export const successRoute = createRoute({
	getParentRoute: () => appRoute,
	path: "/success",
	validateSearch: (
		search: Record<string, unknown>
	): { checkout_id?: string } => ({
		checkout_id:
			typeof search.checkout_id === "string" ? search.checkout_id : undefined,
	}),
	component: SuccessPage,
});

export const libraryRoute = createRoute({
	getParentRoute: () => appRoute,
	path: "/",
	component: LibraryPage,
});

export const pluckRoute = createRoute({
	getParentRoute: () => appRoute,
	path: "/plucks/$pluckId",
	component: PluckDetailPage,
});

export const boardsRoute = createRoute({
	getParentRoute: () => appRoute,
	path: "/boards",
	component: BoardsPage,
});

export const accountRoute = createRoute({
	getParentRoute: () => appRoute,
	path: "/account",
	component: AccountPage,
});

export const boardRoute = createRoute({
	getParentRoute: () => appRoute,
	path: "/boards/$boardId",
	component: BoardPage,
});

const routeTree = rootRoute.addChildren([
	loginRoute,
	appRoute.addChildren([
		libraryRoute,
		pluckRoute,
		boardsRoute,
		boardRoute,
		accountRoute,
		successRoute,
	]),
]);

export const router = createRouter({
	routeTree,
	// The Electron build is served from a static bundle, where hash history
	// avoids needing a server-side SPA fallback for deep links.
	history: isDesktop() ? createHashHistory() : undefined,
	defaultPreload: "intent",
	scrollRestoration: true,
});

declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
